import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useRef, useState, useEffect } from "react";
import Cookies from "js-cookie";
import { LayoutDashboard } from "lucide-react";
import { useStore } from "../../zustand/store";
import appConfig from "../../config/appConfig";
import AffiliateSignupModal from "../../components/affiliate/AffiliateSignupModal";
import IlluRates       from "./illustrations/IlluRates";
import IlluAnalytics   from "./illustrations/IlluAnalytics";
import IlluLinks       from "./illustrations/IlluLinks";
import IlluPayments    from "./illustrations/IlluPayments";
import IlluAttribution from "./illustrations/IlluAttribution";
import IlluTeam        from "./illustrations/IlluTeam";

/* ── Scroll reveal ─────────────────────────────────────────────────────────── */
function useScrollReveal(threshold = 0.12) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return [ref, visible];
}

/* Wraps children in a fade+slide-up reveal. className is applied to the wrapper
   so it can be used as a flex/grid container directly. */
function Reveal({ children, delay = 0, className = '', style = {} }) {
    const [ref, visible] = useScrollReveal();
    return (
        <div
            ref={ref}
            className={`pxl-reveal${visible ? ' pxl-reveal--in' : ''}${className ? ' ' + className : ''}`}
            style={{ '--rd': `${delay}ms`, ...style }}
        >
            {children}
        </div>
    );
}

/* ── Wave divider ──────────────────────────────────────────────────────────── */
function WaveDivider({ topColor = '#fff', bottomColor = '#f9f6ff' }) {
    return (
        <div style={{ background: topColor, lineHeight: 0, display: 'block' }}>
            <svg viewBox="0 0 1440 52" fill="none" xmlns="http://www.w3.org/2000/svg"
                 preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 52 }}>
                <path d="M0,44 C240,8 480,52 720,24 C960,0 1200,48 1440,20 L1440,52 L0,52 Z" fill={bottomColor}/>
            </svg>
        </div>
    );
}

/* ── Hero network illustration ─────────────────────────────────────────────── */
function HeroIllustration() {
    return (
        <div className="pxl-al-hero-illu" aria-hidden="true">
            <svg viewBox="0 0 280 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="pxl-al-illu-svg">
                <defs>
                    <radialGradient id="illuCG" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#9b3fe8"/>
                        <stop offset="100%" stopColor="#6b0fc0"/>
                    </radialGradient>
                    <radialGradient id="illuGlw" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#9b3fe8" stopOpacity="0.18"/>
                        <stop offset="100%" stopColor="#9b3fe8" stopOpacity="0"/>
                    </radialGradient>
                </defs>

                {/* Ambient glow */}
                <circle cx="140" cy="120" r="82" fill="url(#illuGlw)"/>

                {/* Dashed rings */}
                <circle cx="140" cy="120" r="92" stroke="#dcc8f5" strokeWidth="1" strokeDasharray="4 5"/>
                <circle cx="140" cy="120" r="65" stroke="#e8d5f9" strokeWidth="1" strokeDasharray="3 6" opacity="0.6"/>

                {/* Connection lines */}
                <line x1="140" y1="120" x2="42"  y2="44"  stroke="#c8a4f0" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.65"/>
                <line x1="140" y1="120" x2="238" y2="44"  stroke="#c8a4f0" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.65"/>
                <line x1="140" y1="120" x2="42"  y2="200" stroke="#c8a4f0" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.65"/>
                <line x1="140" y1="120" x2="238" y2="200" stroke="#c8a4f0" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.65"/>

                {/* Animated dots traveling outward */}
                <circle r="3.5" fill="#7e1acc">
                    <animateMotion dur="2.4s" repeatCount="indefinite" path="M140,120 L42,44"/>
                </circle>
                <circle r="3.5" fill="#9134de">
                    <animateMotion dur="2.8s" repeatCount="indefinite" begin="0.7s" path="M140,120 L238,44"/>
                </circle>
                <circle r="3" fill="#7e1acc" opacity="0.8">
                    <animateMotion dur="2.6s" repeatCount="indefinite" begin="1.3s" path="M140,120 L42,200"/>
                </circle>
                <circle r="3" fill="#9134de" opacity="0.8">
                    <animateMotion dur="3s" repeatCount="indefinite" begin="0.4s" path="M140,120 L238,200"/>
                </circle>

                {/* Centre circle */}
                <circle cx="140" cy="120" r="44" fill="url(#illuCG)"/>
                {/* Dollar sign */}
                <text x="140" y="120" textAnchor="middle" dominantBaseline="central" fontSize="42" fontWeight="700"
                      fill="white" fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif">
                    $
                </text>

                {/* Outer nodes */}
                <circle cx="42"  cy="44"  r="22" fill="white" stroke="#e4d4f8" strokeWidth="1.5"/>
                <circle cx="238" cy="44"  r="22" fill="white" stroke="#e4d4f8" strokeWidth="1.5"/>
                <circle cx="42"  cy="200" r="22" fill="white" stroke="#e4d4f8" strokeWidth="1.5"/>
                <circle cx="238" cy="200" r="22" fill="white" stroke="#e4d4f8" strokeWidth="1.5"/>

                {/* Person silhouettes */}
                <circle cx="42"  cy="40"  r="7" fill="#c8a8f0"/>
                <path d="M30 58c0-6.6 5.4-12 12-12s12 5.4 12 12"  stroke="#c8a8f0" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                <circle cx="238" cy="40"  r="7" fill="#c8a8f0"/>
                <path d="M226 58c0-6.6 5.4-12 12-12s12 5.4 12 12" stroke="#c8a8f0" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                <circle cx="42"  cy="196" r="7" fill="#c8a8f0"/>
                <path d="M30 214c0-6.6 5.4-12 12-12s12 5.4 12 12"  stroke="#c8a8f0" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                <circle cx="238" cy="196" r="7" fill="#c8a8f0"/>
                <path d="M226 214c0-6.6 5.4-12 12-12s12 5.4 12 12" stroke="#c8a8f0" strokeWidth="1.5" strokeLinecap="round" fill="none"/>

                {/* Badges */}
                <circle cx="59"  cy="29"  r="10" fill="#7e1acc"/>
                <text x="59"  y="33"  textAnchor="middle" fontSize="10" fill="white" fontWeight="700">$</text>
                <circle cx="255" cy="29"  r="10" fill="#7e1acc"/>
                <text x="255" y="33"  textAnchor="middle" fontSize="10" fill="white" fontWeight="700">$</text>
                <circle cx="59"  cy="185" r="10" fill="#2db67c"/>
                <text x="59"  y="189" textAnchor="middle" fontSize="10" fill="white" fontWeight="600">✓</text>
                <circle cx="255" cy="185" r="10" fill="#2db67c"/>
                <text x="255" y="189" textAnchor="middle" fontSize="10" fill="white" fontWeight="600">✓</text>
            </svg>
        </div>
    );
}

/* ── Page ──────────────────────────────────────────────────────────────────── */
export default function AffiliateLanding({ pageTitle }) {
    const navigate = useNavigate();
    const store = useStore(state => state);
    const token = store?.userData?.token;
    const cookieToken = Cookies.get('authToken');
    const isLoggedIn = !!(token && cookieToken);
    const userName = store?.userData?.name || store?.userData?.email || "Account";

    const [signupOpen, setSignupOpen] = useState(false);
    const openSignup = () => setSignupOpen(true);
    const closeSignup = () => setSignupOpen(false);

    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef(null);

    useEffect(() => {
        if (!profileOpen) return;
        const handler = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [profileOpen]);

    const logoutMe = () => {
        store.updateStore("userData", {});
        Cookies.remove('authToken');
        navigate('/');
    };

    return (
        <div className="pxl-al-shell">
            <Helmet><title>{pageTitle || appConfig.pageTitle("Affiliate Program")}</title></Helmet>

            {/* ── Sticky Header ── */}
            <header className="pxl-al-header">
                <div className="pxl-al-header-inner">
                    <Link to="/"><img src={appConfig.appLogo} alt="PixaLab" className="pxl-al-header-logo" /></Link>
                    <nav className="pxl-al-header-nav">
                        <Link to="/pricing" className="pxl-al-nav-link">Pricing</Link>
                        {isLoggedIn ? (
                            <div className="pxl-pub-profile" ref={profileRef}>
                                <button
                                    type="button"
                                    className="pxl-pub-profile-btn"
                                    onClick={() => setProfileOpen(o => !o)}
                                    aria-expanded={profileOpen}
                                >
                                    <span className="pxl-pub-profile-avatar">
                                        {userName.charAt(0).toUpperCase()}
                                    </span>
                                    <span className="pxl-pub-profile-name">{userName}</span>
                                    <svg className="pxl-pub-profile-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                </button>
                                {profileOpen && (
                                    <div className="pxl-pub-profile-dropdown">
                                        <button type="button" className="pxl-pub-profile-item" onClick={() => navigate('/dashboard')}>
                                            <LayoutDashboard size={15} strokeWidth={1.8} />
                                            Dashboard
                                        </button>
                                        <div className="pxl-pub-profile-divider" />
                                        <button type="button" className="pxl-pub-profile-item pxl-pub-profile-item--logout" onClick={logoutMe}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <Link to="/" className="pxl-al-nav-link">Log in</Link>
                                <button type="button" onClick={openSignup} className="pxlCommon-btn pxl-al-header-btn">Apply now</button>
                            </>
                        )}
                    </nav>
                </div>
            </header>

            {/* ── Hero ── */}
            <section className="pxl-al-hero">
                <div className="pxl-al-hero-bg-orb pxl-al-hero-bg-orb--1" />
                <div className="pxl-al-hero-bg-orb pxl-al-hero-bg-orb--2" />
                <div className="pxl-al-wrap pxl-al-hero-inner">
                    <div className="pxl-al-hero-content">
                        <div className="pxl-al-eyebrow">
                            <span className="pxl-al-eyebrow-dot" />
                            PixaLab Affiliate Program
                        </div>
                        <h1 className="pxl-al-hero-h1">
                            Turn your audience<br />
                            into <span className="pxl-al-hero-h1-accent">recurring income</span>
                        </h1>
                        <p className="pxl-al-hero-lead">
                            Earn up to <strong>30% lifetime commission</strong> every time someone subscribes through your link. No quotas, no minimums — get paid every month your referrals stay subscribed.
                        </p>
                        <div className="pxl-al-hero-actions">
                            <button type="button" onClick={openSignup} className="pxlCommon-btn pxl-al-hero-cta">
                                Start earning today
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 8 }}>
                                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/>
                                </svg>
                            </button>
                            <Link to="/affiliate/dashboard" className="pxl-al-secondary-link">
                                Already an affiliate? Log in →
                            </Link>
                        </div>
                        <div className="pxl-al-hero-trust">
                            <span className="pxl-al-trust-item"><IconCheck size={13} /> Free forever</span>
                            <span className="pxl-al-trust-item"><IconCheck size={13} /> Approved in 24 hours</span>
                            <span className="pxl-al-trust-item"><IconCheck size={13} /> Paid monthly via PayPal</span>
                        </div>
                    </div>
                    <div className="pxl-al-hero-right">
                        <HeroIllustration />
                        <div className="pxl-al-hero-stats">
                            <StatBox value="30%"       label="Lifetime commission" icon={<IconPercent />} />
                            <StatBox value="30 days"   label="Cookie window"       icon={<IconClock />} />
                            <StatBox value="Monthly"   label="PayPal payouts"      icon={<IconActivity />} />
                            <StatBox value="Unlimited" label="Earning potential"   icon={<IconInfinity />} />
                        </div>
                    </div>
                </div>
            </section>

            <WaveDivider topColor="#fff" bottomColor="#f9f6ff" />

            {/* ── Features ── */}
            <section className="pxl-al-section pxl-al-features-section">
                <div className="pxl-al-wrap">
                    <Reveal className="pxl-al-section-header">
                        <div className="pxl-al-section-label">Built for creators &amp; marketers</div>
                        <h2 className="pxl-al-section-h2">Everything you need to earn more</h2>
                    </Reveal>
                    <div className="pxl-al-flist">
                        <FeatureRow flip={false} illu={<IlluRates />}
                            tag="Commission" title="Industry-leading rates"
                            desc="Earn up to 30% on every subscription — paid every month your referral stays active. The longer they stay, the more you earn."
                            stat="30% lifetime commission per referral" />
                        <FeatureRow flip={true} illu={<IlluAnalytics />}
                            tag="Analytics" title="Know what's working"
                            desc="See clicks, signups, and revenue update in real time. Spot your best-performing channels and double down on them."
                            stat="Live data — no refresh needed" />
                        <FeatureRow flip={false} illu={<IlluLinks />}
                            tag="Tracking" title="Unlimited custom links"
                            desc="Create dedicated links for blog posts, videos, or campaigns. Each one tracked separately so you know exactly what converts."
                            stat="Every channel tracked independently" />
                        <FeatureRow flip={true} illu={<IlluPayments />}
                            tag="Payments" title="Get paid on time"
                            desc="Reliable monthly PayPal payouts the moment your balance clears the threshold. No chasing invoices, no payment delays."
                            stat="Monthly PayPal payouts, automatically" />
                        <FeatureRow flip={false} illu={<IlluAttribution />}
                            tag="Attribution" title="30-day attribution window"
                            desc="Visitors who don't convert today still earn you commission for 30 days. Capture the long buying cycle, not just impulse clicks."
                            stat="30-day cookie — capture every buyer" />
                        <FeatureRow flip={true} illu={<IlluTeam />}
                            tag="Support" title="A team that has your back"
                            desc="Real humans answer your questions about tracking, payouts, and strategy — usually within hours, never days."
                            stat="~2 hour average response time" />
                    </div>
                </div>
            </section>

            <WaveDivider topColor="#f9f6ff" bottomColor="#fff" />

            {/* ── How it works ── */}
            <section className="pxl-al-section pxl-al-how-section">
                <div className="pxl-al-wrap">
                    <Reveal className="pxl-al-section-header">
                        <div className="pxl-al-section-label">From signup to first payout</div>
                        <h2 className="pxl-al-section-h2">Three steps. No catch.</h2>
                    </Reveal>
                    <Reveal className="pxl-al-steps">
                        <Step num="01" icon={<IconUserPlus />} title="Apply in 60 seconds"
                            desc="Drop us your PayPal email and a quick note about your audience. Most applications are approved within 24 hours — no follow-ups, no hoops." />
                        <div className="pxl-al-step-connector" aria-hidden="true" />
                        <Step num="02" icon={<IconShare />} title="Share your link anywhere"
                            desc="Post it on YouTube, X, your blog, or email list. We auto-track every click and attribute conversions back to you for a full 30 days." />
                        <div className="pxl-al-step-connector" aria-hidden="true" />
                        <Step num="03" icon={<IconDollar />} title="Get paid every month"
                            desc="Commission stacks in your dashboard the moment a referral subscribes. Cash out to PayPal as soon as you hit the minimum — no waiting." />
                    </Reveal>
                </div>
            </section>

            <WaveDivider topColor="#fff" bottomColor="#f9f6ff" />

            {/* ── CTA ── */}
            <section className="pxl-al-section pxl-al-cta-section">
                <div className="pxl-al-wrap">
                    <Reveal>
                        <div className="pxl-al-cta-card">
                            <div className="pxl-al-cta-orb" />
                            <div className="pxl-al-cta-icon"><IconRocket /></div>
                            <h2 className="pxl-al-cta-title">Your audience is already worth money.</h2>
                            <p className="pxl-al-cta-sub">
                                Join the affiliates already turning their reach into recurring revenue with PixaLab. Apply in under a minute and start earning this week.
                            </p>
                            <div className="pxl-al-cta-actions">
                                <button type="button" onClick={openSignup} className="pxlCommon-btn pxl-al-cta-btn">
                                    Become an affiliate →
                                </button>
                                <div className="pxl-al-cta-trust">
                                    <span className="pxl-al-trust-item pxl-al-trust-item--light"><IconCheck size={12} /> No credit card</span>
                                    <span className="pxl-al-trust-item pxl-al-trust-item--light"><IconCheck size={12} /> Approved in 24h</span>
                                    <span className="pxl-al-trust-item pxl-al-trust-item--light"><IconCheck size={12} /> Cancel anytime</span>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="pxl-al-footer">
                <div className="pxl-al-wrap pxl-al-footer-inner">
                    <div className="pxl-al-footer-left">
                        <Link to="/"><img src={appConfig.appLogo} alt="PixaLab" className="pxl-al-footer-logo" /></Link>
                        <span className="pxl-al-footer-copy">© {new Date().getFullYear()} PixaLab. All rights reserved.</span>
                    </div>
                    <div className="pxl-al-footer-links">
                        <Link to="/terms-conditions" className="pxl-al-footer-link">Terms</Link>
                        <Link to="/privacy-policy"   className="pxl-al-footer-link">Privacy</Link>
                        <Link to="/pricing"            className="pxl-al-footer-link">Pricing</Link>
                        {isLoggedIn
                            ? <button type="button" className="pxl-al-footer-link" onClick={logoutMe}>Logout</button>
                            : <Link to="/" className="pxl-al-footer-link">Log in</Link>
                        }
                    </div>
                </div>
            </footer>

            <AffiliateSignupModal show={signupOpen} onHide={closeSignup} />
        </div>
    );
}

/* ── Sub-components ────────────────────────────────────────────────────────── */

function StatBox({ value, label, icon }) {
    return (
        <div className="pxl-al-stat-box">
            <div className="pxl-al-stat-box-icon">{icon}</div>
            <div className="pxl-al-stat-box-value">{value}</div>
            <div className="pxl-al-stat-box-label">{label}</div>
        </div>
    );
}

/* ── Feature row (zig-zag) ─────────────────────────────────────────────────── */
function FeatureRow({ illu, flip, tag, title, desc, stat }) {
    return (
        <Reveal className={`pxl-al-frow${flip ? ' pxl-al-frow--flip' : ''}`}>
            <div className="pxl-al-frow-illu">{illu}</div>
            <div className="pxl-al-frow-body">
                <span className="pxl-al-frow-tag">{tag}</span>
                <h3 className="pxl-al-frow-title">{title}</h3>
                <p className="pxl-al-frow-desc">{desc}</p>
                {stat && <div className="pxl-al-frow-stat"><span className="pxl-al-frow-stat-dot" />{stat}</div>}
            </div>
        </Reveal>
    );
}

function Step({ num, icon, title, desc }) {
    return (
        <div className="pxl-al-step">
            <div className="pxl-al-step-num">{num}</div>
            <div className="pxl-al-step-icon">{icon}</div>
            <div className="pxl-al-step-title">{title}</div>
            <div className="pxl-al-step-desc">{desc}</div>
        </div>
    );
}

/* ── Icons ─────────────────────────────────────────────────────────────────── */
function IconCheck({ size = 14 }) {
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}
/* ── Feature illustrations (static) ─────────────────────────────────────── */


function IconInfinity() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12c-2-2.5-4-4-6-4a4 4 0 0 0 0 8c2 0 4-1.5 6-4z"/><path d="M12 12c2 2.5 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.5-6 4z"/></svg>;
}
function IconPercent() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>;
}
function IconClock() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
}
function IconActivity() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
}
function IconUserPlus() {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>;
}
function IconShare() {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>;
}
function IconDollar() {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
}
function IconRocket() {
    return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>;
}
